import type { ReactNode } from 'react';
import { IconAlert, IconCheck, IconInfo } from '@/components/Icons';

export type AlertTone = 'info' | 'success' | 'warning' | 'error';

const TONE_CLASS: Record<AlertTone, string> = {
  info: 'border-slate-200 bg-surface text-slate-700',
  success: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  warning: 'border-amber-300 bg-amber-50 text-amber-900',
  error: 'border-rose-200 bg-rose-50 text-rose-800',
};

const ICON_CLASS: Record<AlertTone, string> = {
  info: 'text-slate-400',
  success: 'text-emerald-600',
  warning: 'text-amber-600',
  error: 'text-rose-600',
};

export interface AlertProps {
  tone?: AlertTone;
  title?: string;
  children?: ReactNode;
  className?: string;
}

export function Alert({ tone = 'info', title, children, className = '' }: AlertProps) {
  const Icon = tone === 'success' ? IconCheck : tone === 'info' ? IconInfo : IconAlert;
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex gap-3 rounded-lg border px-4 py-3 text-sm ${TONE_CLASS[tone]} ${className}`.trim()}
    >
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${ICON_CLASS[tone]}`} />
      <div className="min-w-0 space-y-1">
        {title ? <p className="font-semibold">{title}</p> : null}
        {children ? <div className="leading-relaxed">{children}</div> : null}
      </div>
    </div>
  );
}
